import { useCallback, useEffect, useRef, useState } from "react";
import { theme, card, btnPrimary, btnSecondary, inputStyle } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import {
  BUG_CATEGORY_LABELS, BUG_SEVERITIES, BUG_STATUSES, BUG_STATUS_LABELS, fmtWhen, severityColours, statusColours,
  type BugCategory, type BugSeverity, type BugStatus,
} from "../lib/bugReports";
import { ThreadItem } from "./BugReportButton";
import { AttachmentGallery, ScreenshotPicker, releaseShots, uploadShots, type BugAttachment, type PendingShot } from "./BugReportScreenshots";

// Platform Admin → Bug Reports. The platform administrators' ticket queue:
// every report a client, consultant or auditor raised from the top bar. Triage
// (status, severity, category, assignee), reply to the reporter, keep internal
// notes, resolve and close. Rules in backend/src/lib/bugReports.ts.

type StatusFilter = "open" | "all" | BugStatus;

interface QueueRow {
  id: string;
  reference: string;
  title: string;
  category: BugCategory;
  severity: BugSeverity;
  status: BugStatus;
  reporterName: string;
  reporterEmail: string;
  reporterRole: string;
  createdAt: string;
  lastActivityAt: string;
  organization: { id: string; name: string } | null;
  assignee: { id: string; displayName: string } | null;
  _count: { messages: number };
}

interface TicketMessage {
  id: string;
  authorName: string;
  fromAdmin: boolean;
  internal: boolean;
  body: string;
  statusChange: BugStatus | null;
  createdAt: string;
}

interface Ticket {
  id: string;
  reference: string;
  title: string;
  description: string;
  stepsToReproduce: string | null;
  expectedResult: string | null;
  category: BugCategory;
  severity: BugSeverity;
  status: BugStatus;
  reporterId: string | null;
  reporterName: string;
  reporterEmail: string;
  reporterRole: string;
  organization: { id: string; name: string; edition: string } | null;
  consultancy: { id: string; name: string } | null;
  bureau: { id: string; name: string } | null;
  pageUrl: string | null;
  appVersion: string | null;
  userAgent: string | null;
  viewport: string | null;
  diagnostics: Array<{ at: string; message: string }> | null;
  assigneeId: string | null;
  resolution: string | null;
  resolvedAt: string | null;
  closedAt: string | null;
  createdAt: string;
  messages: TicketMessage[];
  attachments: BugAttachment[];
}

const STATUS_FILTERS: Array<{ key: StatusFilter; label: string }> = [
  { key: "open", label: "Open" },
  { key: "new", label: "New" },
  { key: "in_progress", label: "In progress" },
  { key: "awaiting_reporter", label: "Awaiting reporter" },
  { key: "resolved", label: "Resolved" },
  { key: "closed", label: "Closed" },
  { key: "all", label: "All" },
];

function Pill({ text, color, bg }: { text: string; color: string; bg: string }) {
  return <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", padding: "2px 8px", borderRadius: 3, color, background: bg, whiteSpace: "nowrap" }}>{text}</span>;
}

const selectStyle: React.CSSProperties = { ...inputStyle, width: "auto", padding: "6px 10px", fontSize: 12.5 };
const fieldLabel: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: theme.color.subtle, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 };

export function BugReportsAdminTab({ initialTicketId, onCountsChanged }: { initialTicketId?: string | null; onCountsChanged?: () => void }) {
  const [status, setStatus] = useState<StatusFilter>(initialTicketId ? "all" : "open");
  const [severity, setSeverity] = useState<BugSeverity | "">("");
  const [role, setRole] = useState<"" | "client" | "consultant" | "auditor">("");
  const [assignee, setAssignee] = useState<"" | "me" | "none">("");
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState<QueueRow[] | null>(null);
  const [counts, setCounts] = useState<Record<BugStatus, number> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(initialTicketId ?? null);

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const loadQueue = useCallback(() => {
    api.get<{ reports: QueueRow[]; counts: Record<BugStatus, number> }>("/admin/bug-reports", {
      status, severity: severity || undefined, role: role || undefined, assignee: assignee || undefined, q: query || undefined,
    })
      .then((r) => { setRows(r.reports); setCounts(r.counts); setError(null); })
      .catch((e) => setError(apiErrorMessage(e, "The bug reports could not be loaded.")));
  }, [status, severity, role, assignee, query]);
  useEffect(loadQueue, [loadQueue]);

  const changed = useCallback(() => { loadQueue(); onCountsChanged?.(); }, [loadQueue, onCountsChanged]);

  const openCount = counts ? counts.new + counts.triaged + counts.in_progress + counts.awaiting_reporter : 0;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: theme.color.heading }}>Bug reports</div>
        <div style={{ fontSize: 12.5, color: theme.color.muted }}>
          Raised by clients, consultants and auditors from the top bar on every edition.
          {counts && ` ${openCount} open, ${counts.new} new.`}
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
        {STATUS_FILTERS.map((f) => {
          const active = status === f.key;
          const n = counts ? (f.key === "open" ? openCount : f.key === "all" ? Object.values(counts).reduce((a, b) => a + b, 0) : counts[f.key]) : null;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setStatus(f.key)}
              style={{
                fontSize: 12, padding: "5px 11px", borderRadius: 3, cursor: "pointer",
                border: `1px solid ${active ? theme.color.accent : theme.color.border}`,
                background: active ? theme.color.accentBg : "transparent",
                color: active ? theme.color.accentText : theme.color.muted, fontWeight: active ? 600 : 500,
              }}
            >
              {f.label}{n !== null ? ` (${n})` : ""}
            </button>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        <input style={{ ...inputStyle, maxWidth: 280 }} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, reporter, organisation, BUG-0042" aria-label="Search bug reports" />
        <select style={selectStyle} value={severity} onChange={(e) => setSeverity(e.target.value as BugSeverity | "")} aria-label="Severity">
          <option value="">Any severity</option>
          {BUG_SEVERITIES.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
        </select>
        <select style={selectStyle} value={role} onChange={(e) => setRole(e.target.value as typeof role)} aria-label="Reporter role">
          <option value="">Any reporter</option>
          <option value="client">Clients</option>
          <option value="consultant">Consultants</option>
          <option value="auditor">Auditors</option>
        </select>
        <select style={selectStyle} value={assignee} onChange={(e) => setAssignee(e.target.value as typeof assignee)} aria-label="Assignee">
          <option value="">Anyone</option>
          <option value="me">Assigned to me</option>
          <option value="none">Unassigned</option>
        </select>
      </div>

      {error && <div role="alert" style={{ color: theme.color.dangerText, fontSize: 13, marginBottom: 12 }}>{error}</div>}

      <div style={{ display: "grid", gridTemplateColumns: selected ? "minmax(280px, 2fr) minmax(360px, 3fr)" : "1fr", gap: 16, alignItems: "start" }}>
        <div style={{ ...card, padding: 0, overflow: "hidden" }}>
          {!rows && <div style={{ padding: 16, color: theme.color.subtle, fontSize: 13 }}>Loading...</div>}
          {rows && rows.length === 0 && <div style={{ padding: 24, color: theme.color.subtle, fontSize: 13, textAlign: "center" }}>No bug reports match.</div>}
          {rows?.map((r) => {
            const s = statusColours(r.status);
            const v = severityColours(r.severity);
            const active = r.id === selected;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => setSelected(r.id)}
                style={{
                  display: "block", width: "100%", textAlign: "left", font: "inherit", color: "inherit", cursor: "pointer",
                  padding: "11px 14px", border: "none", borderBottom: `1px solid ${theme.color.border}`,
                  borderLeft: `3px solid ${active ? theme.color.accent : "transparent"}`,
                  background: active ? theme.color.accentBg : "transparent",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontVariantNumeric: "tabular-nums", color: theme.color.subtle }}>{r.reference}</span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: r.status === "new" ? 700 : 600, color: theme.color.heading, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.title}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <Pill text={BUG_STATUS_LABELS[r.status]} {...s} />
                  <Pill text={r.severity} {...v} />
                  <span style={{ fontSize: 11.5, color: theme.color.subtle }}>
                    {r.reporterName} ({r.reporterRole}){r.organization ? ` · ${r.organization.name}` : ""} · {fmtWhen(r.lastActivityAt)}
                    {r.assignee ? ` · ${r.assignee.displayName}` : " · unassigned"}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
        {selected && <TicketDetail key={selected} id={selected} onChanged={changed} onClose={() => setSelected(null)} />}
      </div>
    </div>
  );
}

function TicketDetail({ id, onChanged, onClose }: { id: string; onChanged: () => void; onClose: () => void }) {
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [admins, setAdmins] = useState<Array<{ id: string; displayName: string }>>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);
  const [replyStatus, setReplyStatus] = useState<BugStatus | "">("");
  const [resolution, setResolution] = useState("");
  const [shots, setShots] = useState<PendingShot[]>([]);
  const shotsRef = useRef(shots);
  shotsRef.current = shots;
  useEffect(() => () => releaseShots(shotsRef.current), []);

  const load = useCallback(() => {
    api.get<{ report: Ticket; admins: Array<{ id: string; displayName: string }> }>(`/admin/bug-reports/${encodeURIComponent(id)}`)
      .then((r) => { setTicket(r.report); setAdmins(r.admins); setResolution(r.report.resolution ?? ""); setError(null); })
      .catch((e) => setError(apiErrorMessage(e, "This ticket could not be loaded.")));
  }, [id]);
  useEffect(load, [load]);

  async function patch(body: Record<string, unknown>) {
    setBusy(true);
    try {
      await api.patch(`/admin/bug-reports/${encodeURIComponent(id)}`, body);
      load();
      onChanged();
    } catch (e) {
      setError(apiErrorMessage(e, "The change could not be saved."));
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      const res = await api.post<{ messageId: string }>(`/admin/bug-reports/${encodeURIComponent(id)}/messages`, { body: reply, internal, status: !internal && replyStatus ? replyStatus : undefined });
      setReply("");
      setReplyStatus("");
      const failed = await uploadShots(`/admin/bug-reports/${encodeURIComponent(id)}/attachments`, shots, res.messageId);
      releaseShots(shots);
      setShots([]);
      if (failed.length) setError(`The message was sent, but some screenshots were not: ${failed.join("; ")}`);
      load();
      onChanged();
    } catch (e) {
      setError(apiErrorMessage(e, "The message could not be sent."));
    } finally {
      setBusy(false);
    }
  }

  if (error && !ticket) return <div style={card}><div style={{ color: theme.color.dangerText, fontSize: 13 }}>{error}</div></div>;
  if (!ticket) return <div style={card}><div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading...</div></div>;

  const where = [ticket.organization?.name, ticket.consultancy?.name, ticket.bureau?.name].filter(Boolean).join(" · ");
  const galleryPath = `/admin/bug-reports/${encodeURIComponent(id)}/attachments`;
  const onReport = ticket.attachments.filter((a) => !a.messageId);

  return (
    <div style={{ ...card, padding: 18 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontVariantNumeric: "tabular-nums", color: theme.color.subtle }}>{ticket.reference} · raised {fmtWhen(ticket.createdAt)}</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: theme.color.heading, marginTop: 2, wordBreak: "break-word" }}>{ticket.title}</div>
        </div>
        <button type="button" style={{ ...btnSecondary, padding: "5px 10px", fontSize: 12 }} onClick={onClose}>Close panel</button>
      </div>

      {/* Triage */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 14 }}>
        <div>
          <div style={fieldLabel}>Status</div>
          <select style={{ ...selectStyle, width: "100%" }} value={ticket.status} disabled={busy} onChange={(e) => patch({ status: e.target.value })}>
            {BUG_STATUSES.map((s) => <option key={s} value={s}>{BUG_STATUS_LABELS[s]}</option>)}
          </select>
        </div>
        <div>
          <div style={fieldLabel}>Severity</div>
          <select style={{ ...selectStyle, width: "100%" }} value={ticket.severity} disabled={busy} onChange={(e) => patch({ severity: e.target.value })}>
            {BUG_SEVERITIES.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
          </select>
        </div>
        <div>
          <div style={fieldLabel}>Category</div>
          <select style={{ ...selectStyle, width: "100%" }} value={ticket.category} disabled={busy} onChange={(e) => patch({ category: e.target.value })}>
            {(Object.keys(BUG_CATEGORY_LABELS) as BugCategory[]).map((c) => <option key={c} value={c}>{BUG_CATEGORY_LABELS[c]}</option>)}
          </select>
        </div>
        <div>
          <div style={fieldLabel}>Assignee</div>
          <select style={{ ...selectStyle, width: "100%" }} value={ticket.assigneeId ?? ""} disabled={busy} onChange={(e) => patch({ assigneeId: e.target.value || null })}>
            <option value="">Unassigned</option>
            {admins.map((a) => <option key={a.id} value={a.id}>{a.displayName}</option>)}
          </select>
        </div>
      </div>

      {/* Reporter and context */}
      <div style={{ background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, borderRadius: theme.radius.sm, padding: "10px 12px", marginBottom: 14, fontSize: 12.5, color: theme.color.text, lineHeight: 1.6 }}>
        <div>
          <strong>{ticket.reporterName}</strong> ({ticket.reporterRole}) · <a href={`mailto:${ticket.reporterEmail}`} style={{ color: theme.color.accentText }}>{ticket.reporterEmail}</a>
          {!ticket.reporterId && <span style={{ color: theme.color.subtle }}> · account no longer exists</span>}
        </div>
        {where && <div>{where}{ticket.organization && ticket.organization.edition !== "standard" ? ` (${ticket.organization.edition} edition)` : ""}</div>}
        {ticket.pageUrl && <div style={{ wordBreak: "break-all" }}>Page: <code>{ticket.pageUrl}</code></div>}
        {(ticket.appVersion || ticket.viewport) && <div>{ticket.appVersion ? `Version ${ticket.appVersion}` : ""}{ticket.appVersion && ticket.viewport ? " · " : ""}{ticket.viewport ? `Window ${ticket.viewport}` : ""}</div>}
        {ticket.userAgent && <div style={{ color: theme.color.subtle, fontSize: 11.5, wordBreak: "break-word" }}>{ticket.userAgent}</div>}
        {ticket.diagnostics && ticket.diagnostics.length > 0 && (
          <details style={{ marginTop: 6 }}>
            <summary style={{ cursor: "pointer", fontWeight: 600 }}>{ticket.diagnostics.length} browser error{ticket.diagnostics.length === 1 ? "" : "s"} before the report</summary>
            <div style={{ fontFamily: "monospace", fontSize: 11.5, marginTop: 6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {ticket.diagnostics.map((d, i) => <div key={i}>{d.at ? `${fmtWhen(d.at)}  ` : ""}{d.message}</div>)}
            </div>
          </details>
        )}
      </div>

      <Block title="What happened">{ticket.description}</Block>
      {ticket.stepsToReproduce && <Block title="Steps to reproduce">{ticket.stepsToReproduce}</Block>}
      {ticket.expectedResult && <Block title="Expected">{ticket.expectedResult}</Block>}
      {onReport.length > 0 && <Block title={`Screenshots (${onReport.length})`}><AttachmentGallery attachments={onReport} basePath={galleryPath} /></Block>}

      {/* Resolution note, sent to the reporter when the ticket is resolved or closed */}
      <div style={{ marginBottom: 14 }}>
        <div style={fieldLabel}>Resolution</div>
        <textarea style={{ ...inputStyle, minHeight: 50, resize: "vertical" }} value={resolution} maxLength={8000} onChange={(e) => setResolution(e.target.value)} placeholder="What was wrong and what fixed it (shown to the reporter)" />
        <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
          <button type="button" style={{ ...btnSecondary, padding: "6px 12px", fontSize: 12 }} disabled={busy || resolution === (ticket.resolution ?? "")} onClick={() => patch({ resolution })}>Save note</button>
          {ticket.status !== "resolved" && ticket.status !== "closed" && (
            <button type="button" style={{ ...btnPrimary, padding: "6px 12px", fontSize: 12 }} disabled={busy} onClick={() => patch({ status: "resolved", resolution })}>Mark resolved</button>
          )}
          {ticket.status !== "closed" && (
            <button type="button" style={{ ...btnSecondary, padding: "6px 12px", fontSize: 12 }} disabled={busy} onClick={() => patch({ status: "closed", resolution })}>Close ticket</button>
          )}
          {ticket.status === "closed" && (
            <button type="button" style={{ ...btnSecondary, padding: "6px 12px", fontSize: 12 }} disabled={busy} onClick={() => patch({ status: "in_progress" })}>Reopen</button>
          )}
        </div>
      </div>

      <div style={{ ...fieldLabel, marginTop: 4 }}>Conversation</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
        {ticket.messages.length === 0 && <div style={{ fontSize: 12.5, color: theme.color.subtle }}>No messages yet.</div>}
        {ticket.messages.map((m) => (
          <ThreadItem key={m.id} m={m} labels={BUG_STATUS_LABELS} mine={m.fromAdmin} internal={m.internal}>
            <AttachmentGallery attachments={ticket.attachments.filter((a) => a.messageId === m.id)} basePath={galleryPath} />
          </ThreadItem>
        ))}
      </div>

      {error && <div role="alert" style={{ color: theme.color.dangerText, fontSize: 12.5, marginBottom: 8 }}>{error}</div>}
      <textarea
        style={{ ...inputStyle, minHeight: 80, resize: "vertical", ...(internal ? { borderStyle: "dashed", background: theme.color.warningBg } : {}) }}
        value={reply}
        maxLength={8000}
        onChange={(e) => setReply(e.target.value)}
        placeholder={internal ? "Internal note - only platform administrators see this" : `Reply to ${ticket.reporterName} (emailed to them)`}
        aria-label="Reply"
      />
      <div style={{ marginTop: 8 }}><ScreenshotPicker shots={shots} onChange={setShots} compact /></div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: theme.color.text, cursor: "pointer" }}>
          <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} />
          Internal note
        </label>
        {!internal && (
          <select style={selectStyle} value={replyStatus} onChange={(e) => setReplyStatus(e.target.value as BugStatus | "")} aria-label="Status after reply">
            <option value="">Keep status</option>
            <option value="awaiting_reporter">and ask the reporter</option>
            <option value="in_progress">and mark in progress</option>
            <option value="resolved">and mark resolved</option>
            <option value="closed">and close</option>
          </select>
        )}
        <div style={{ flex: 1 }} />
        <button type="button" style={{ ...btnPrimary, opacity: busy || !reply.trim() ? 0.6 : 1 }} disabled={busy || !reply.trim()} onClick={send}>
          {internal ? "Add note" : "Send reply"}
        </button>
      </div>
    </div>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={fieldLabel}>{title}</div>
      <div style={{ fontSize: 13, color: theme.color.text, whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.55 }}>{children}</div>
    </div>
  );
}
